/** Chart.js heeft echte kleurwaarden nodig; styles.css blijft de enige bron. */
export function token(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

export type Palette = {
  /**
   * Het vlak waar alles op ligt. Navy kent geen kaarten meer: dit is de pagina
   * zelf, en omdat die met het weer meekleurt schrijft background.ts de exacte
   * waarde bij elke verf mee. Alles wat een gat in een lijn moet prikken — een
   * stip, een plakkende tabelkop — heeft precies deze kleur nodig.
   */
  ground: string;
  ink: string;
  ink2: string;
  ink3: string;
  ink4: string;
  grid: string;
  coral: string;
  coral2: string;
  amber: string;
  indigo: string;
  violet: string;
  dashed: string;
  rain: string;
  /** Gedempt vlak: aanwezig, maar niet om naar te kijken. */
  muted: string;
  dotOff: string;
  curve: string;
  tickline: string;
  /** Zeven gevalideerde jaarkleuren, in vaste volgorde. */
  years: string[];
};

export function palette(): Palette {
  return {
    ground: token('--ground'),
    ink: token('--ink'),
    ink2: token('--ink-2'),
    ink3: token('--ink-3'),
    ink4: token('--ink-4'),
    grid: token('--grid'),
    coral: token('--coral'),
    coral2: token('--coral-2'),
    amber: token('--amber'),
    indigo: token('--indigo'),
    violet: token('--violet'),
    dashed: token('--dashed'),
    rain: token('--rain'),
    muted: token('--muted'),
    dotOff: token('--dot-off'),
    curve: token('--curve'),
    tickline: token('--tickline'),
    years: [1, 2, 3, 4, 5, 6, 7].map((i) => token(`--y${i}`)),
  };
}

/* --- themakeuze -------------------------------------------------------- */

export type ThemeChoice = 'system' | 'light' | 'dark';
export type Theme = 'light' | 'dark';

/** Zelfde sleutel als het blokje in index.html dat de flits voorkomt. */
const STORAGE_KEY = 'weerstation:thema';

const media = window.matchMedia('(prefers-color-scheme: dark)');
const listeners = new Set<() => void>();

function stored(): ThemeChoice {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === 'light' || value === 'dark') return value;
  } catch {
    /* privémodus kan localStorage blokkeren; dan volgen we gewoon het systeem */
  }
  return 'system';
}

/** Wat de gebruiker koos: 'system' zolang die het aan het toestel overlaat. */
export function themeChoice(): ThemeChoice {
  return stored();
}

/** Wat er daadwerkelijk op het scherm staat. */
export function theme(): Theme {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

/**
 * Zet de kleur van de balk boven de pagina op een telefoon.
 *
 * Eén plek, want twee bestanden schrijven erin: hier bij een themawissel, en
 * background.ts zodra de achtergrond het weer volgt. De waarde moet een echte
 * kleur zijn — een lege string maakt de metatag ongeldig en dan valt de browser
 * terug op zijn eigen wit, wat in de donkere modus precies de streep oplevert
 * die we hier proberen weg te werken.
 */
export function setThemeColour(colour: string): void {
  if (!colour) return;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', colour);
}

function paint(): void {
  const choice = stored();
  const next: Theme = choice === 'system' ? (media.matches ? 'dark' : 'light') : choice;
  const changed = document.documentElement.dataset.theme !== next;

  document.documentElement.dataset.theme = next;
  /*
   * De balk boven de pagina kleurt mee. Hier de kale paginakleur van het nieuwe
   * thema, en niet `--ground`: staat de weerachtergrond aan, dan houdt die nog
   * de kleur van het vórige thema vast tot background.ts opnieuw verft. Beter
   * één tel de effen paginakleur van het juiste thema dan de weerkleur van het
   * verkeerde — main.ts roept applyWeatherBackground meteen hierna aan.
   */
  setThemeColour(token('--page'));

  if (changed) for (const fn of listeners) fn();
}

export function setThemeChoice(choice: ThemeChoice): void {
  try {
    if (choice === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    /* niet kunnen bewaren mag het wisselen niet tegenhouden */
  }
  paint();
}

/**
 * Roept `fn` na elke themawissel aan.
 *
 * Nodig omdat Chart.js met echte kleurwaarden tekent en niet met tokens: een
 * canvas dat al staat weet niets van een gewijzigde custom property. Alles wat in
 * CSS staat past zichzelf aan; alles op canvas moet opnieuw.
 */
export function onThemeChange(fn: () => void): void {
  listeners.add(fn);
}

// Staat de keuze op 'systeem', dan volgt de pagina het toestel meteen — ook als
// dat midden in de sessie omslaat, zoals bij een nachtstand op een vast tijdstip.
media.addEventListener('change', () => {
  if (stored() === 'system') paint();
});

// index.html heeft data-theme al gezet; dit zet alleen de theme-color goed.
paint();

export function alpha(hex: string, a: number): string {
  const m = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex.trim());
  if (!m) return hex;
  const [r, g, b] = [m[1]!, m[2]!, m[3]!].map((h) => Number.parseInt(h, 16));
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

type Area = { left: number; right: number; top: number; bottom: number } | undefined;

/** Horizontaal verloop, zoals de max- en min-lijn in het ontwerp. */
export function strokeGradient(
  ctx: CanvasRenderingContext2D,
  area: Area,
  from: string,
  to: string,
): CanvasGradient | string {
  if (!area) return to;
  const g = ctx.createLinearGradient(area.left, 0, area.right, 0);
  g.addColorStop(0, from);
  g.addColorStop(1, to);
  return g;
}

/**
 * Verticaal verloop voor de band tussen dagmaximum en dagminimum.
 *
 * Navy vult die band met één kleur die naar onderen uitdooft — 16 % bovenaan,
 * 8 % onderaan — in plaats van met twee tinten. Zo blijft het een schaduw onder
 * de twee lijnen en geen derde reeks die om aandacht vraagt.
 */
export function bandGradient(
  ctx: CanvasRenderingContext2D,
  area: Area,
  p: Palette,
): CanvasGradient | string {
  if (!area) return alpha(p.coral, 0.16);
  const g = ctx.createLinearGradient(0, area.top, 0, area.bottom);
  g.addColorStop(0, alpha(p.coral, 0.16));
  g.addColorStop(1, alpha(p.coral, 0.08));
  return g;
}

/** Assen in Navy-stijl: alleen zachte horizontale lijnen, geen randen. */
export function axes(p: Palette, suffix = '') {
  return {
    x: {
      grid: { display: false },
      border: { display: false },
      ticks: { color: p.ink4, maxRotation: 0, autoSkipPadding: 18 },
    },
    y: {
      grid: { color: p.grid, lineWidth: 1.4, drawTicks: false },
      border: { display: false },
      ticks: {
        color: p.ink4,
        padding: 10,
        ...(suffix ? { callback: (v: string | number) => `${v}${suffix}` } : {}),
      },
    },
  };
}
